import axios, { AxiosError, AxiosHeaders } from "axios";
import type { ApiError } from "@/shared/api/types";

const TOKEN_KEY = "zekabin.token";
const USER_KEY = "zekabin.user";

export const tokenStorage = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

export const userStorage = {
  get: () => {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw || raw === "undefined") return null;
    try { return JSON.parse(raw); } catch { return null; }
  },
  set: (user: unknown) => localStorage.setItem(USER_KEY, JSON.stringify(user)),
};

export const apiClient = axios.create({
  baseURL: "/api",
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use((config) => {
  const token = tokenStorage.get();
  if (token) {
    const headers = AxiosHeaders.from(config.headers ?? {});
    headers.set("Authorization", `Bearer ${token}`);
    if (config.data instanceof FormData) {
      headers.delete("Content-Type");
    }
    config.headers = headers;
  }
  return config;
});

apiClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      tokenStorage.clear();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    // responseType: "blob" uçlarında (ör. PDF indirme) hata gövdesi de Blob olur; JSON'a çevir ki
    // extractApiError gerçek hata mesajını (message) okuyabilsin, ham JSON yerine.
    const data = error.response?.data as unknown;
    if (data instanceof Blob && data.type.includes("json")) {
      try {
        error.response!.data = JSON.parse(await data.text());
      } catch {
        /* JSON değilse dokunma */
      }
    }
    return Promise.reject(error);
  }
);

export const extractApiError = (error: unknown): string => {
  if (axios.isAxiosError<ApiError>(error)) {
    return error.response?.data?.message ?? "Sunucu hatası oluştu";
  }
  if (error instanceof Error) return error.message;
  return "Beklenmeyen bir hata oluştu";
};

/** Sunucu DomainException `code` alanı (ör. "SECTION_EXISTS"); axios olmayan hatada null. */
export const apiErrorCode = (error: unknown): string | null =>
  axios.isAxiosError<ApiError>(error) ? error.response?.data?.code ?? null : null;
